import { throwDevelopmentOrLog } from '~/modules/utilities/cross_env_utils';

export interface FocusResult {
    shouldPreventDefault: boolean;
}

export interface FocusTrap {
    focusNext: () => FocusResult;
    focusPrevious: () => FocusResult;
    focusFirstElement: () => void;
    // @ts-expect-error * Automated Type Error Fix
    focusElementByIndex: (index) => void;
    getFocusableElementsQuantity: () => number;
}

// @ts-expect-error * Automated Type Error Fix
export function getFocusTrap(element): FocusTrap {
    let selectedIndex = 0;
    function getFocusAbleElements() {
        const allFocuseableChildren = getAllFocusAbleChildElements(element);
        return {
            focusAbleItems: allFocuseableChildren,
            firstFocusableEl: allFocuseableChildren[0],
            lastFocusableEl: allFocuseableChildren[allFocuseableChildren.length - 1],
            elementsQuantity: allFocuseableChildren.length,
        };
    }
    return {
        focusNext() {
            selectedIndex += 1;
            const { elementsQuantity, firstFocusableEl } = getFocusAbleElements();
            if (selectedIndex > elementsQuantity - 1) {
                selectedIndex = 0;
                // @ts-expect-error * Automated Type Error Fix
                firstFocusableEl?.focus();
                return { shouldPreventDefault: true };
            }
            return { shouldPreventDefault: false };
        },
        focusPrevious() {
            selectedIndex -= 1;
            const { elementsQuantity, lastFocusableEl } = getFocusAbleElements();
            if (selectedIndex < 0) {
                selectedIndex = elementsQuantity - 1;
                // @ts-expect-error * Automated Type Error Fix
                lastFocusableEl?.focus();
                return { shouldPreventDefault: true };
            }
            return { shouldPreventDefault: false };
        },
        focusFirstElement() {
            const { firstFocusableEl } = getFocusAbleElements();
            selectedIndex = 0;
            // @ts-expect-error * Automated Type Error Fix
            firstFocusableEl?.focus();
        },
        focusElementByIndex(index: number) {
            const { focusAbleItems } = getFocusAbleElements();
            if (!focusAbleItems[index]) {
                throwDevelopmentOrLog(new Error(`Failed to find focusable element with index: ${index}`));
                return;
            }
            selectedIndex = index;
            // @ts-expect-error * Automated Type Error Fix
            focusAbleItems[index].focus();
        },
        getFocusableElementsQuantity() {
            const { elementsQuantity } = getFocusAbleElements();
            return elementsQuantity;
        },
    };
}

export async function asyncGetFocusTrap(parent: any, element: string): Promise<FocusTrap | undefined> {
    const waitForElementToLoad = (): Promise<void> => {
        return new Promise((resolve, reject) => {
            const observer = new MutationObserver(() => {
                const loadedElement = parent.querySelector(element);
                if (loadedElement) {
                    observer.disconnect();
                    resolve();
                }
            });

            observer.observe(parent, { childList: true, subtree: true });

            // Timeout handling
            setTimeout(() => {
                observer.disconnect();
                reject(`Timeout: ${element} not loaded.`);
            }, 5000);
        });
    };
    try {
        await waitForElementToLoad();
    } catch (ex) {
        throwDevelopmentOrLog(ex);
    }
    const target = parent.querySelector(element);
    return target ? getFocusTrap(target) : undefined;
}

// @ts-expect-error * Automated Type Error Fix
export function getAllFocusAbleChildElements(element, allFocuseableChildren = []) {
    const children = getDirectChildren(element, allFocuseableChildren);

    for (const child of children) {
        getAllFocusAbleChildElements(child, allFocuseableChildren);
    }

    return allFocuseableChildren;
}

// @ts-expect-error * Automated Type Error Fix
function getDirectChildren(element, allFocuseableChildren) {
    const children = [];

    // LWC components should not be focusable and if we have a shadowRoot, we are an LWC component
    if (element.shadowRoot) {
        children.push(...Array.from(element.shadowRoot.children));
    } else {
        const isSlot = element instanceof HTMLSlotElement;

        if (isSlot) {
            // If we're a slot, we may or may not be assigned elements via the slot attribute.
            // If we're assigned slot elements, then those elements replace our children, so to get those, we call the assignedElements function.
            // If we're not assigned slot elements, then we render our chilren and largely behave like any other HTML element.
            const assignedElements = Array.from(element.assignedElements() as HTMLElement[]);
            if (assignedElements.length > 0) {
                children.push(...assignedElements);
            } else if (element.children) {
                children.push(...Array.from(element.children));
            }
        } else if (element.children) {
            children.push(...Array.from(element.children));
        }

        if (element.tabIndex >= 0) {
            allFocuseableChildren.push(element);
        }
    }

    return children;
}
